import React, { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import moment from "moment"
import { useToast } from "~/utils/Toast"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchDropdown from "~/library/CatchDropdown"
import ConfirmationModal from "~/library/ConfirmationModal"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiMenus } from "~/routes"
import Menu from "~/types/Menu"
import LineItem from "~/types/LineItem"
import { formatCurrency } from "~/utils/helpers"
import { ArrowLeft } from "lucide-react"
import NavigationLink from "~/library/NavigationLink"
import {
  getMenusBackPath,
  getMenusBackTitle,
  clearMenusNavigationState,
} from "~/utils/navigationState"

const MenuView = () => {
  const [isDeleting, setIsDeleting] = useState(false)
  const navigate = useNavigate()
  const { venueSlug } = getAppConfig()
  const { id } = useParams<{ id: string }>()
  const toast = useToast()

  const endpoint = apiMenus.show.path({ venueSlug, id: id! })
  const { data: menu, isLoading } = useFetchResource<{ menu: Menu }, "menu">(endpoint, "menu", {})

  const renderDetail = (title: string, content: React.ReactNode, size = 6) => {
    return (
      <div className={`col-${size || 6}`}>
        <p className="mt-2 mb-1 text-muted fw-bold font-12 text-uppercase">{title}</p>
        <div className="d-flex">
          <div className="mt-1 font-14 mb-2">{content}</div>
        </div>
      </div>
    )
  }

  const deleteMenu = () => {
    setIsDeleting(true)
    const req = apiMenus.destroy({ data: { venueSlug, id: id! } })
    req
      .then(() => {
        toast("Menu deleted successfully", "success")
        clearMenusNavigationState()
        navigate("/menus")
      })
      .catch((res) => {
        toast(
          `Error deleting menu. ${res.body?.errors?.join(", ") || "Please try again"}`,
          "danger"
        )
        setIsDeleting(false)
      })
  }

  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageTitle
        title={
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-link p-0 me-2"
              onClick={() => navigate(getMenusBackPath())}
              aria-label={`Go back to ${getMenusBackTitle()}`}
              title={`Go back to ${getMenusBackTitle()}`}
            >
              <ArrowLeft />
            </button>
            <span>{menu?.name}</span>
          </div>
        }
      >
        <CatchDropdown
          label="Actions"
          items={[
            {
              label: "Edit Menu",
              to: `/menus/${id}/edit`,
            },
            {
              label: "Delete Menu",
              className: "text-danger",
              onClick: () => setIsDeleting(true),
            },
          ]}
        />
      </PageTitle>

      <div className="row">
        <div className="col-8">
          <CatchCard title="Menu Details">
            <div className="row">
              {renderDetail("Name", menu?.name, 6)}
              {renderDetail(
                "Status",
                <span className={`badge badge-lg ${menu?.active ? "bg-success" : "bg-secondary"}`}>
                  {menu?.active ? "Active" : "Inactive"}
                </span>,
                6
              )}
            </div>
          </CatchCard>

          <CatchCard title="Line Items">
            {isLoading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span className="ms-2">Loading line items...</span>
              </div>
            ) : menu?.groupedLineItems && Object.keys(menu.groupedLineItems).length > 0 ? (
              <GroupedLineItemsList groupedLineItems={menu.groupedLineItems} />
            ) : (
              <div className="text-center py-4">
                <p className="text-muted mb-2">No line items are assigned to this menu yet.</p>
              </div>
            )}
          </CatchCard>
        </div>

        <div className="col-4">
          <CatchCard title="Internal Details">
            <div className="row">
              {renderDetail("Created on", moment(menu?.createdAt).format("MMM Do YYYY hh:mma"), 12)}
              {menu?.updatedAt &&
                renderDetail(
                  "Updated on",
                  moment(menu?.updatedAt).format("MMM Do YYYY hh:mma"),
                  12
                )}
            </div>
          </CatchCard>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleting}
        onClose={() => setIsDeleting(false)}
        onConfirm={deleteMenu}
        title={`Delete Menu "${menu?.name}"?`}
        description="This action cannot be undone. Line items in this menu will be unassigned. Are you sure you want to delete this menu?"
      />
    </LoadingWrapper>
  )
}

// Grouped Line Items List Component
interface GroupedLineItemsProps {
  groupedLineItems: { [categoryName: string]: LineItem[] }
}

const GroupedLineItemsList: React.FC<GroupedLineItemsProps> = ({ groupedLineItems }) => {
  return (
    <div>
      {Object.entries(groupedLineItems).map(([categoryName, items]) => (
        <CategorySection key={categoryName} categoryName={categoryName} items={items} />
      ))}
    </div>
  )
}

// Category Section Component
interface CategorySectionProps {
  categoryName: string
  items: LineItem[]
}

const CategorySection: React.FC<CategorySectionProps> = ({ categoryName, items }) => {
  const itemCount = items?.length || 0

  if (itemCount === 0) return null

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
        <h6 className="fw-bold text-dark mb-0">{categoryName || "Uncategorized"}</h6>
        <span className="badge bg-primary bg-opacity-10 text-primary">
          {itemCount} item{itemCount !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="row">
        {items.map((lineItem) => (
          <LineItemCard key={lineItem.uuid} lineItem={lineItem} />
        ))}
      </div>
    </div>
  )
}

// Line Item Card Component
interface LineItemCardProps {
  lineItem: LineItem
}

const LineItemCard: React.FC<LineItemCardProps> = ({ lineItem }) => {
  return (
    <div className="col-12 mb-3">
      <div className="d-flex align-items-center p-3 border rounded-2 bg-light bg-opacity-50">
        <div className="flex-grow-1">
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1 me-3">
              <NavigationLink
                type="line-items"
                to={`/line-items/${lineItem.id}`}
                style={{ width: "fit-content" }}
                className="fw-bold text-decoration-none mb-1 d-block"
              >
                {lineItem.name}
              </NavigationLink>
              {lineItem.minOrderQty && lineItem.minOrderQty > 1 && (
                <div className="small text-muted">(minimum order of {lineItem.minOrderQty})</div>
              )}
            </div>
            <div className="text-end ms-3">
              <span className="fw-bold text-primary fs-5">
                {formatCurrency({
                  cents: lineItem.amountCents,
                  currency_iso: "USD",
                })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MenuView
